import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { X, Send, Sparkles, Plus, Copy, Loader2, WifiOff, MessageSquarePlus } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { GhostIcon } from "@/components/ghost/GhostIcon";

// Derive authorized admin page context from the current route.
function adminContext(pathname) {
  let m = pathname.match(/^\/admin\/reservations\/([^/]+)$/);
  if (m) return { context_type: "reservation", record_id: m[1] };
  m = pathname.match(/^\/admin\/events\/([^/]+)$/);
  if (m) return { context_type: "event", record_id: m[1] };
  return { context_type: "", record_id: null };
}

export default function GhostLauncher({ mode = "guest" }) {
  const loc = useLocation();
  const nav = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(() => sessionStorage.getItem(`ghost_open_${mode}`) === "1");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [convId, setConvId] = useState(null);
  const [online, setOnline] = useState(navigator.onLine);
  const [inq, setInq] = useState(null);
  const [bubble, setBubble] = useState(() => mode === "public" && typeof window !== "undefined" && sessionStorage.getItem("ghost_bubble_dismissed") !== "1");
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    api.get("/ghost/config").then((r) => setCfg(r.data)).catch(() => setCfg(false));
    const on = () => setOnline(true), off = () => setOnline(false);
    window.addEventListener("online", on); window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  useEffect(() => { sessionStorage.setItem(`ghost_open_${mode}`, open ? "1" : "0"); }, [open, mode]);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, sending]);
  useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);

  if (!cfg) return null;
  const audienceOk = mode === "admin" ? (cfg.enabled && cfg.admin_enabled)
    : mode === "public" ? (cfg.enabled && cfg.public_enabled)
    : (cfg.enabled && cfg.guest_enabled);
  if (!audienceOk) return null;

  const name = cfg.display_name || "D";
  const PUBLIC_PROMPTS = cfg.public_quick_prompts || ["What is The D?", "Show me the experiences",
    "How do I request an invitation?", "Can I host a private event?", "Can I stay overnight?",
    "Can I reserve the pool?", "How do reservations work?", "Tell me about membership",
    "Help me plan a celebration", "What is the weather at The D?", "Contact The D"];
  const greeting = mode === "admin" ? cfg.admin_greeting : cfg.greeting;
  const prompts = mode === "admin" ? cfg.admin_quick_prompts
    : mode === "public" ? PUBLIC_PROMPTS : cfg.guest_quick_prompts;

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    if (!online) { toast.error(`${name} is offline. Please reconnect to continue.`); return; }
    const next = [...messages, { role: "user", content: msg }];
    setMessages(next); setInput(""); setSending(true);
    try {
      const body = { messages: next, conversation_id: convId };
      let res;
      if (mode === "admin") {
        res = await api.post("/ghost/admin/chat", { ...body, ...adminContext(loc.pathname) });
      } else if (mode === "public") {
        res = await api.post("/ghost/public/chat", { ...body, page: loc.pathname });
      } else {
        res = await api.post("/ghost/guest/chat", body);
      }
      const d = res.data;
      setConvId(d.conversation_id);
      setMessages([...next, { role: "assistant", content: d.reply, fallback: d.fallback, question: msg }]);
    } catch (e) {
      setMessages([...next, { role: "assistant", content: apiErr(e), fallback: true, question: msg }]);
    } finally { setSending(false); }
  };

  const newConversation = () => { setMessages([]); setConvId(null); setInput(""); };
  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Copied."); };

  const INQ_TYPES = [["general", "General inquiry"], ["invitation", "Request an invitation"],
    ["reservation", "Start a reservation request"], ["event", "Private-event inquiry"],
    ["membership", "Membership inquiry"], ["contact", "Contact The D"]];
  const openInq = (type) => { setInq({ type, name: "", email: "", phone: "", interest: "", why_join: "", message: "", consent: false, review: false }); setOpen(true); };
  const submitInq = async () => {
    if (!inq.name.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(inq.email)) { toast.error("Please add your name and a valid email."); return; }
    if (!inq.consent) { toast.error("Please confirm consent to be contacted."); return; }
    if (!inq.review) { setInq({ ...inq, review: true }); return; }
    try {
      const { data } = await api.post("/ghost/public/inquiry", { ...inq, conversation_id: convId, message: inq.message || inq.interest });
      toast.success("Inquiry sent to The D.");
      setMessages((m) => [...m, { role: "assistant", content: data.message || "Thank you — your inquiry has reached The D. We'll follow up personally." }]);
      setInq(null);
    } catch (e) { toast.error(apiErr(e)); }
  };

  // Fallback actions when AI is unavailable — preserve the guest/admin request safely.
  const sendToConcierge = async (question) => {
    try { await api.post("/portal/concierge", { category: "General request", priority: "normal", notes: question, desired_time: "" });
      toast.success("Sent to The D concierge."); } catch (e) { toast.error(apiErr(e)); }
  };
  const saveAsTask = async (question) => {
    try { await api.post("/admin/tasks", { title: `From Ghost: ${question.slice(0, 80)}`, category: "general", priority: "normal", notes: question });
      toast.success("Saved as a task."); } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <>
      {!open && (
        <div className={`fixed z-[60] flex flex-col items-end gap-2 ${mode === "admin" ? "bottom-5 right-5" : "bottom-24 right-5"}`}>
          {mode === "public" && bubble && (
            <div className="relative glass-dark border rounded-2xl rounded-br-none pl-4 pr-6 py-2.5 max-w-[230px] shadow-xl"
              style={{ borderColor: "rgba(212,175,55,0.35)" }} data-testid="ghost-invite-bubble">
              <button onClick={() => { setBubble(false); sessionStorage.setItem("ghost_bubble_dismissed", "1"); }}
                aria-label="Dismiss" className="absolute top-1.5 right-1.5 h-5 w-5 rounded-full flex items-center justify-center text-muted-foreground hover:text-gold">
                <X size={12} />
              </button>
              <button onClick={() => { setOpen(true); setBubble(false); }} className="text-left" data-testid="ghost-bubble-open">
                <p className="text-xs text-foreground/90 leading-snug">Questions about The D? <span className="text-gold">Ask D.</span></p>
              </button>
            </div>
          )}
          <button
            onClick={() => { setOpen(true); setBubble(false); }}
            data-testid={`ghost-launcher-${mode}`}
            aria-label="Open D, The D's private AI concierge"
            className="group flex items-center gap-2 pl-2 pr-4 py-2 min-h-[48px] rounded-full glass-dark border hover:glow-pool transition-shadow"
            style={{ borderColor: "rgba(212,175,55,0.4)" }}
          >
            <GhostIcon size={34} />
            <span className="text-xs tracking-label uppercase text-gold hidden sm:inline">Ask {name}</span>
          </button>
        </div>
      )}

      {open && (
        <div
          role="dialog" aria-label={`${name} — ${cfg.subtitle}`} aria-modal="false"
          data-testid={`ghost-panel-${mode}`}
          className="fixed z-[60] flex flex-col glass-dark border rounded-none sm:rounded-2xl overflow-hidden
                     inset-0 sm:inset-auto sm:bottom-5 sm:right-5 sm:w-[400px] sm:h-[600px] sm:max-h-[85vh]"
          style={{ borderColor: "rgba(212,175,55,0.35)" }}
          onKeyDown={(e) => { if (e.key === "Escape") setOpen(false); }}
        >
          <header className="flex items-center justify-between px-4 py-3 border-b border-gold/20 shrink-0">
            <div className="flex items-center gap-3">
              <GhostIcon size={38} />
              <div className="leading-tight">
                <p className="font-serif text-lg text-gold">{name}</p>
                <p className="text-[10px] tracking-label uppercase text-muted-foreground">{cfg.subtitle}</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={newConversation} aria-label="Start a new conversation" data-testid="ghost-new-convo" className="p-2 rounded-md text-muted-foreground hover:text-gold hover:bg-white/5"><Plus size={16} /></button>
              <button onClick={() => setOpen(false)} aria-label="Close Ghost" data-testid="ghost-close" className="p-2 rounded-md text-muted-foreground hover:text-gold hover:bg-white/5"><X size={18} /></button>
            </div>
          </header>

          {cfg.status !== "configured" && (
            <div className="px-4 py-2 text-[11px] text-amber-300/90 bg-amber-400/5 border-b border-amber-400/20">
              {name} is temporarily unavailable. You can still {mode === "admin" ? "save requests as tasks" : "reach the concierge"} below.
            </div>
          )}
          {!online && (
            <div className="px-4 py-2 text-[11px] text-muted-foreground bg-white/5 border-b border-white/10 flex items-center gap-2">
              <WifiOff size={13} /> You are offline. {name} needs a connection.
            </div>
          )}

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4" data-testid="ghost-messages">
            {messages.length === 0 && (
              <div className="space-y-4">
                <div className="flex gap-3">
                  <GhostIcon size={30} className="shrink-0" glow={false} />
                  <p className="text-sm text-foreground/90 glass rounded-xl rounded-tl-none px-4 py-3">{greeting}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {(prompts || []).map((p, i) => (
                    <button key={i} onClick={() => send(p)} data-testid={`ghost-quick-${i}`}
                      className="text-xs text-pool border border-pool/30 rounded-full px-3 py-1.5 hover:bg-pool/10 transition-colors text-left">
                      {p}
                    </button>
                  ))}
                </div>
                {mode === "public" && (
                  <div className="flex flex-wrap gap-2 pt-3 mt-1 border-t border-white/10" data-testid="public-action-bar">
                    <button onClick={() => nav("/experiences")} data-testid="public-act-experiences" className="text-xs text-gold border border-gold/40 rounded-full px-3 py-1.5 hover:bg-gold/10">Explore Experiences</button>
                    <button onClick={() => nav("/request-invitation")} data-testid="public-act-invite" className="text-xs text-gold border border-gold/40 rounded-full px-3 py-1.5 hover:bg-gold/10">Request an Invitation</button>
                    <button onClick={() => openInq("reservation")} data-testid="public-act-book" className="text-xs text-gold border border-gold/40 rounded-full px-3 py-1.5 hover:bg-gold/10">Start a Reservation Request</button>
                    <button onClick={() => openInq("event")} data-testid="public-act-event" className="text-xs text-gold border border-gold/40 rounded-full px-3 py-1.5 hover:bg-gold/10">Plan a Private Event</button>
                    <button onClick={() => nav("/register")} data-testid="public-act-access" className="text-xs text-gold border border-gold/40 rounded-full px-3 py-1.5 hover:bg-gold/10">Request Access</button>
                    <button onClick={() => nav("/contact")} data-testid="public-act-contact" className="text-xs text-gold border border-gold/40 rounded-full px-3 py-1.5 hover:bg-gold/10">Contact The D</button>
                  </div>
                )}
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                {m.role === "assistant" && <GhostIcon size={30} className="shrink-0" glow={false} />}
                <div className={`max-w-[85%] ${m.role === "user" ? "text-right" : ""}`}>
                  <div className={`text-sm whitespace-pre-wrap px-4 py-3 rounded-xl ${m.role === "user"
                    ? "bg-gold/15 text-foreground rounded-tr-none" : "glass text-foreground/90 rounded-tl-none"}`}>
                    {m.content}
                  </div>
                  {m.role === "assistant" && !m.fallback && (
                    <button onClick={() => copy(m.content)} className="text-[10px] text-muted-foreground hover:text-gold mt-1 inline-flex items-center gap-1"><Copy size={11} /> Copy</button>
                  )}
                  {m.role === "assistant" && m.fallback && (
                    mode === "admin" ? (
                      <button onClick={() => saveAsTask(m.question)} data-testid="ghost-save-task" className="text-[11px] text-pool hover:underline mt-1 inline-flex items-center gap-1"><MessageSquarePlus size={12} /> Save my request as a task</button>
                    ) : (
                      <span className="mt-1 inline-flex flex-col gap-1">
                        {mode === "public"
                          ? <button onClick={() => openInq("general")} data-testid="public-fallback-inquiry" className="text-[11px] text-pool hover:underline inline-flex items-center gap-1"><MessageSquarePlus size={12} /> Submit an inquiry to The D</button>
                          : <button onClick={() => sendToConcierge(m.question)} data-testid="ghost-to-concierge" className="text-[11px] text-pool hover:underline inline-flex items-center gap-1"><MessageSquarePlus size={12} /> Send my request to the concierge</button>}
                        {cfg.public_contact_email && <a href={`mailto:${cfg.public_contact_email}`} className="text-[11px] text-gold hover:underline" data-testid="ghost-contact-email">Or email The D at {cfg.public_contact_email}</a>}
                      </span>
                    )
                  )}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex gap-3"><GhostIcon size={30} className="shrink-0" glow={false} />
                <div className="glass rounded-xl rounded-tl-none px-4 py-3 flex items-center gap-2 text-muted-foreground text-sm"><Loader2 size={14} className="animate-spin" /> {name} is thinking…</div>
              </div>
            )}
          </div>

          {mode === "public" && inq && (
            <div className="border-t border-gold/20 p-3 space-y-2 shrink-0 max-h-[62%] overflow-y-auto" data-testid="public-inquiry-form">
              <div className="flex justify-between items-center"><p className="text-xs uppercase tracking-label text-gold">Send an inquiry to The D</p><button onClick={() => setInq(null)} aria-label="Close inquiry"><X size={14} /></button></div>
              {inq.review ? (
                <div className="text-sm space-y-1" data-testid="inquiry-review">
                  <p className="text-xs text-muted-foreground">Please review before sending:</p>
                  <p><span className="text-muted-foreground">Type:</span> {(INQ_TYPES.find((t) => t[0] === inq.type) || [])[1]}</p>
                  <p><span className="text-muted-foreground">Name:</span> {inq.name}</p>
                  <p><span className="text-muted-foreground">Email:</span> {inq.email}</p>
                  {inq.phone && <p><span className="text-muted-foreground">Phone:</span> {inq.phone}</p>}
                  {inq.interest && <p><span className="text-muted-foreground">Interest:</span> {inq.interest}</p>}
                  {inq.why_join && <p><span className="text-muted-foreground">Why The D:</span> {inq.why_join}</p>}
                  {inq.message && <p><span className="text-muted-foreground">Message:</span> {inq.message}</p>}
                  <p className="text-[10px] text-muted-foreground pt-1">Your details will be sent to The D for personal follow-up.</p>
                  <div className="flex gap-2 pt-2">
                    <button onClick={submitInq} className="btn-gold px-4 py-2 text-xs" data-testid="inquiry-confirm">Confirm &amp; Send</button>
                    <button onClick={() => setInq({ ...inq, review: false })} className="btn-ghost-gold px-4 py-2 text-xs">Edit</button>
                  </div>
                </div>
              ) : (
                <>
                  <select value={inq.type} onChange={(e) => setInq({ ...inq, type: e.target.value })} className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="inquiry-type">{INQ_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
                  <input value={inq.name} onChange={(e) => setInq({ ...inq, name: e.target.value })} placeholder="Your name*" className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="inquiry-name" />
                  <input value={inq.email} onChange={(e) => setInq({ ...inq, email: e.target.value })} placeholder="Your email*" className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="inquiry-email" />
                  <input value={inq.phone} onChange={(e) => setInq({ ...inq, phone: e.target.value })} placeholder="Phone (optional)" className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm" />
                  <input value={inq.interest} onChange={(e) => setInq({ ...inq, interest: e.target.value })} placeholder="Area of interest / preferred dates" className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm" />
                  <textarea value={inq.why_join} onChange={(e) => setInq({ ...inq, why_join: e.target.value })} rows={2} placeholder="Why would you love to experience The D?" className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="inquiry-why-join" />
                  <textarea value={inq.message} onChange={(e) => setInq({ ...inq, message: e.target.value })} rows={2} placeholder="Message (optional)" className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm" />
                  <label className="flex items-start gap-2 text-xs cursor-pointer"><input type="checkbox" checked={inq.consent} onChange={(e) => setInq({ ...inq, consent: e.target.checked })} className="accent-gold mt-0.5" data-testid="inquiry-consent" /> I agree to be contacted by The D. See the <a href="/policies" className="text-pool underline ml-1">Privacy Policy</a>.</label>
                  <button onClick={submitInq} className="btn-gold w-full py-2 text-sm" data-testid="inquiry-review-btn">Review &amp; Send</button>
                </>
              )}
            </div>
          )}

          <div className="px-4 py-1.5 text-center text-[10px] tracking-label uppercase text-muted-foreground/70 border-t border-white/5 shrink-0" data-testid="ghost-attribution">
            {cfg.attribution || "Powered by Prestigious Companies"}
          </div>

          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-gold/20 shrink-0 flex items-end gap-2">
            <textarea
              ref={inputRef} rows={1} value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={`Message ${name}…`} data-testid="ghost-input" aria-label={`Message ${name}`}
              className="flex-1 resize-none bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm focus:border-gold focus:outline-none max-h-28"
            />
            <button type="submit" disabled={sending || !input.trim()} aria-label="Send message" data-testid="ghost-send"
              className="btn-gold p-2.5 rounded-xl disabled:opacity-40"><Send size={17} /></button>
          </form>
        </div>
      )}
    </>
  );
}
